<script setup>
import { computed, ref } from 'vue'
import { useQuasar } from 'quasar'
import i18n from '@/plugins/i18n'

const $q = useQuasar()
const t = i18n.global.t

// Chiave nuova: finche' non e' in tabella localizations si usa il testo inglese.
const FALLBACKS = {
  'scheduled.list.activateHint': 'Turn on the switch to activate this message'
}

const tr = (key) => (i18n.global.te(key) ? i18n.global.t(key) : (FALLBACKS[key] || key))

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  // Vero solo per il messaggio appena creato, e solo per una decina di secondi: nasce spento
  // e senza un segnale nessuno capisce che va attivato.
  highlightToggle: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['toggle', 'edit', 'delete'])

const onDelete = (item, reset) => {
  $q.dialog({
    title: t('common.confirmDelete'),
    message: t('scheduled.confirmDeleteMessage'),
    cancel: true,
    persistent: true
  }).onOk(() => {
    emit('delete', item)
  }).onCancel(() => {
    reset()
  }).onDismiss(() => {
    reset()
  })
}

const expanded = ref(false)

const borderClass = computed(() => {
  if (props.item.failure_reason) return 'border-error'
  if (!props.item.active) return null
  return 'border-active'
})

const allChips = computed(() => {
  const contacts = (props.item.recipient_contacts || []).map((contact, index) => ({
    key: `c_${index}`,
    color: 'primary',
    name: contact
  }))

  const groups = (props.item.recipient_groups || []).map((group, index) => ({
    key: `g_${index}`,
    color: group.active ? 'secondary' : 'negative',
    name: group.display_name
  }))

  const numbers = (props.item.recipient_numbers || []).map((num, index) => ({
    key: `n_${index}`,
    color: 'grey-7',
    name: num
  }))

  return [...contacts, ...groups, ...numbers]
})

const visibleChips = computed(() => {
  if (expanded.value || allChips.value.length <= 6) {
    return allChips.value
  }
  return allChips.value.slice(0, 5)
})
</script>

<template>
  <q-card
    class="bg-white"
    :class="borderClass"
  >
    <q-card-section class="row items-center justify-between no-wrap">
      <div class="text-subtitle1 text-weight-medium ellipsis col">
        {{ item.name }}
      </div>

      <div class="row items-center q-gutter-x-sm no-wrap flex-shrink-0">
        <q-toggle
          :model-value="item.active"
          color="primary"
          dense
          :class="{ 'toggle-pulse': props.highlightToggle && !item.active }"
          :label="$t(item.active ? 'scheduled.status.active' : 'scheduled.status.inactive')"
          @update:model-value="val => emit('toggle', item, val)"
        />

        <q-btn
          flat
          round
          dense
          color="grey"
          icon="mdi-pencil"
          @click="emit('edit', item)"
        >
          <q-tooltip>{{ $t('common.edit') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          dense
          color="negative"
          icon="mdi-delete"
          @click="onDelete(item, () => {})"
        >
          <q-tooltip>{{ $t('common.delete') }}</q-tooltip>
        </q-btn>
      </div>
    </q-card-section>

    <q-card-section>
      <div class="row q-gutter-xs">
        <q-chip
          v-for="c in visibleChips"
          :key="c.key"
          dense
          outline
          :color="c.color"
        >
          {{ c.name }}
        </q-chip>

        <q-chip
          v-if="!expanded && allChips.length > 6"
          dense
          clickable
          color="primary"
          text-color="white"
          icon-right="mdi-chevron-down"
          @click="expanded = true"
        >
          +{{ allChips.length - 5 }}
        </q-chip>

        <q-chip
          v-if="expanded && allChips.length > 6"
          dense
          clickable
          flat
          color="grey-7"
          icon="mdi-chevron-up"
          @click="expanded = false"
        />

        <span
          v-if="allChips.length === 0"
          class="text-caption text-grey-5 text-italic q-ml-xs"
        >
          {{ $t('scheduled.list.noRecipients') }}
        </span>
      </div>
    </q-card-section>

    <q-card-section
      v-if="props.highlightToggle && !item.active"
      class="q-pt-none"
    >
      <div class="row items-center no-wrap text-caption text-primary">
        <q-icon
          name="mdi-toggle-switch-off-outline"
          size="18px"
          class="q-mr-xs"
        />
        <span>{{ tr('scheduled.list.activateHint') }}</span>
      </div>
    </q-card-section>

    <q-card-section class="text-caption text-grey-8">
      <div class="row items-center justify-between">
        <div v-if="item.nextRun">
          {{ $t('scheduled.list.nextRun') }}:
          <span class="text-weight-bold">{{ item.nextRun || '-' }}</span>
          {{ item.timezone ? `(${item.timezone})` : '' }}
        </div>

        <div
          v-if="item.failure_reason"
          class="text-negative row items-center"
        >
          {{ $t(item.failure_reason) }}
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>

<style scoped>
/* Solo per il messaggio appena creato e solo per una decina di secondi (il timer sta nella
   pagina, non qui): un battito sul toggle spento, che si ferma da solo. */
.toggle-pulse {
  animation: toggle-pulse 1.3s ease-in-out infinite;
  transform-origin: center;
}

.toggle-pulse:hover {
  animation-play-state: paused;
}

/* Niente aloni colorati: scala e opacita' non dipendono dal tema e si vedono su qualunque sfondo. */
@keyframes toggle-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.1); opacity: 0.7; }
}

@media (prefers-reduced-motion: reduce) {
  .toggle-pulse { animation: none; }
}
</style>
